import React, {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {ChevronRight, ArrowLeft, Store, Sun, Moon} from 'lucide-react';
import useAuthStore from '../../stores/authStore';
import secureStorage from '../../utils/secureStorage';

const SelectAccount = () => {
    const navigate = useNavigate();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDarkMode, setIsDarkMode] = useState(() => {
        return localStorage.getItem("darkMode") === "true";
    });

    // Use ref to track if initialization has already happened
    const hasInitialized = useRef(false);

    // Get store values without causing re-renders
    const isAuthenticated = useAuthStore(state => state.isAuthenticated);
    const selectAccount = useAuthStore(state => state.selectAccount);

    // Apply dark mode effect
    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
        localStorage.setItem("darkMode", isDarkMode.toString());
    }, [isDarkMode]);

    const toggleDarkMode = () => {
        setIsDarkMode(prev => !prev);
    };

    useEffect(() => {
        // Prevent multiple initializations
        if (hasInitialized.current) {
            return;
        }

        const initializeComponent = async () => {
            if (!isAuthenticated) {
                navigate('/login');
                return;
            }

            try {
                const storedAccounts = await secureStorage.getItem('accounts');

                if (!storedAccounts || storedAccounts.length === 0) {
                    navigate('/login');
                    return;
                }

                setAccounts(storedAccounts);
                setLoading(false);
                hasInitialized.current = true;
            } catch (error) {
                console.error('Error loading accounts:', error);
                navigate('/login');
            }
        };

        initializeComponent();
    }, [isAuthenticated, navigate]);

    const handleSelectAccount = async (account) => {
        await selectAccount(account);
        const lastRoute = localStorage.getItem("lastRoute");
        navigate(lastRoute && lastRoute !== '/select-account' ? lastRoute : '/activite');
    };

    if (loading) {
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 relative overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 dark:bg-primary-900 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-30 animate-blob"></div>
                <div
                    className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 dark:bg-blue-900 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
            </div>

            {/* Dark mode toggle */}
            <div className="absolute top-6 right-6 z-10">
                <button
                    onClick={toggleDarkMode}
                    className="p-2.5 rounded-xl bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border border-gray-200 dark:border-gray-700"
                    aria-label="Toggle Dark Mode"
                >
                    {isDarkMode ?
                        <Sun size={20} className="text-yellow-500"/> :
                        <Moon size={20} className="text-gray-600"/>
                    }
                </button>
            </div>

            {/* Main content */}
            <div className="relative z-10 w-full max-w-md">
                {/* Logo */}
                <div className="mb-8 text-center">
                    <img src="/quickcom-logo-text.png" alt="QuickCom Logo" className="h-10 mx-auto"/>
                </div>

                {/* Card */}
                <div
                    className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl p-6 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            Sélectionner un magasin
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Choisissez un magasin pour continuer
                        </p>
                    </div>

                    {/* Accounts list */}
                    <div className="space-y-2.5 max-h-[400px] overflow-y-auto scrollbar-hide no-scrollbar"
                         style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
                        {accounts.map((account) => (
                            <button
                                key={account.id}
                                onClick={() => handleSelectAccount(account)}
                                className="group w-full p-3.5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50
                                           border border-gray-200 dark:border-gray-600 rounded-2xl hover:border-primary-400 dark:hover:border-primary-500
                                           flex items-center justify-between transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <div className="flex items-center min-w-0 flex-1">
                                    {account.logo ? (
                                        <img
                                            src={account.logo}
                                            alt={account.name}
                                            className="w-11 h-11 rounded-xl mr-3.5 object-cover shadow-md ring-2 ring-gray-100 dark:ring-gray-600 group-hover:ring-primary-400"
                                        />
                                    ) : (
                                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600
                                                       text-primary dark:text-white flex items-center justify-center mr-3.5 shadow-md ring-2 ring-primary-100 dark:ring-primary-900
                                                       group-hover:from-primary-600 group-hover:to-primary-700 transition-all duration-300">
                                            <Store size={20}/>
                                        </div>
                                    )}
                                    <div className="text-left min-w-0 flex-1">
                                        <h3 className="font-semibold text-gray-900 dark:text-white text-base truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                            {account.name}
                                        </h3>
                                        {account.address && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                {account.address}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <ChevronRight
                                    className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0"/>
                            </button>
                        ))}
                    </div>

                    {/* Back button */}
                    <button
                        onClick={() => {
                            localStorage.clear();
                            navigate('/login');
                        }}
                        className="w-full mt-5 py-2.5 text-gray-600 dark:text-gray-400 text-sm flex
                                  justify-center items-center hover:bg-gray-100 dark:hover:bg-gray-700/50
                                  rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] font-medium"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2"/>
                        Retour à la connexion
                    </button>
                </div>

                {/* Footer */}
                <div className="mt-6 text-center text-xs text-gray-500 dark:text-gray-500">
                    © {new Date().getFullYear()} Quickcom. Tous droits réservés.
                </div>
            </div>
        </div>
    );
};

export default SelectAccount;
